import { apiUrl, authenticatedFetch } from './auth';
import type {
    PaginatedWebhookErrors,
    ReplayResult,
    WebhookErrorDetail,
    WebhookErrorListFilters,
    WebhookErrorStatus,
} from '~/types/webhookError';

const BASE = `${apiUrl}/v2/webhook-errors`;

const buildQuery = (filters: WebhookErrorListFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.source) params.set('source', filters.source);
    if (filters.status) params.set('status', String(filters.status));
    if (filters.http_status) params.set('http_status', String(filters.http_status));
    if (filters.search) params.set('search', filters.search);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.ordering) params.set('ordering', filters.ordering);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
};

/**
 * Errors carry the HTTP status so callers can tell a dead-on-arrival 401/403
 * apart from a transient 5xx and skip retrying the former.
 */
const requestFailed = (message: string, status: number) => {
    const error: any = new Error(message);
    error.status = status;
    return error;
};

export const fetchWebhookErrors = async (
    filters: WebhookErrorListFilters = {}
): Promise<PaginatedWebhookErrors> => {
    const resp = await authenticatedFetch(`${BASE}/${buildQuery(filters)}`);
    if (!resp.ok)
        throw requestFailed(
            `Failed to fetch webhook errors (${resp.status})`,
            resp.status
        );
    return resp.json();
};

export const fetchWebhookErrorUnreadCount = async (): Promise<number> => {
    // background: this runs on a timer, so a success must not extend the session.
    const resp = await authenticatedFetch(
        `${BASE}/unread-count/`,
        {},
        { background: true }
    );
    if (!resp.ok)
        throw requestFailed(
            `Failed to fetch unread count (${resp.status})`,
            resp.status
        );
    const json = await resp.json();
    return Number(json.count) || 0;
};

export const fetchWebhookErrorDetail = async (
    id: number
): Promise<WebhookErrorDetail> => {
    const resp = await authenticatedFetch(`${BASE}/${id}/`);
    if (!resp.ok)
        throw requestFailed(
            `Failed to fetch webhook error ${id}`,
            resp.status
        );
    return resp.json();
};

export const updateWebhookError = async (
    id: number,
    patch: { status?: WebhookErrorStatus; resolution_notes?: string }
): Promise<WebhookErrorDetail> => {
    const resp = await authenticatedFetch(`${BASE}/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
    });
    if (!resp.ok)
        throw requestFailed(
            `Failed to update webhook error ${id}`,
            resp.status
        );
    return resp.json();
};

export const bulkUpdateWebhookErrors = async (
    ids: number[],
    patch: { status?: WebhookErrorStatus; resolution_notes?: string }
): Promise<{ updated: number }> => {
    const resp = await authenticatedFetch(`${BASE}/bulk-update/`, {
        method: 'POST',
        body: JSON.stringify({ ids, ...patch }),
    });
    if (!resp.ok)
        throw requestFailed(
            `Failed to bulk update webhook errors (${resp.status})`,
            resp.status
        );
    return resp.json();
};

export const replayWebhookError = async (
    id: number,
    autoResolve: boolean = true
): Promise<ReplayResult> => {
    const resp = await authenticatedFetch(`${BASE}/${id}/replay/`, {
        method: 'POST',
        body: JSON.stringify({ auto_resolve: autoResolve }),
    });
    if (!resp.ok)
        throw requestFailed(
            `Failed to replay webhook error ${id}`,
            resp.status
        );
    return resp.json();
};
